import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {useQueryClient} from 'react-query'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {WithChildren} from '../../../../_metronic/helpers'
import {ModuleProvider, useModule} from '../../../platform/modules'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined, roleIndex?: number) => void
  saveAuthRoles: (roles: AuthModel[], preferredUserRoleId?: number) => AuthModel | undefined
  switchRole: (userRoleId: number | undefined, legacyRoleIndex: number) => Promise<boolean>
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState: AuthContextProps = {
  auth: authHelper.getActiveAuth(),
  saveAuth: () => {},
  saveAuthRoles: () => undefined,
  switchRole: async () => false,
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => useContext(AuthContext)

const AuthStateProvider: FC<WithChildren> = ({children}) => {
  const queryClient = useQueryClient()
  const {activateModule, isModuleSession} = useModule()
  const [auth, setAuth] = useState<AuthModel | undefined>(() => authHelper.getActiveAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()

  const saveAuth = useCallback(
    (nextAuth: AuthModel | undefined, roleIndex?: number) => {
      setAuth(nextAuth)
      if (!nextAuth) {
        authHelper.removeAuthRoles()
        return
      }

      if (nextAuth.moduleCode) {
        activateModule(nextAuth.moduleCode)
      }

      if (typeof roleIndex === 'number') {
        authHelper.setActiveRole(roleIndex)
        return
      }

      authHelper.setAuthRoles({roles: [{...nextAuth}], activeRoleIndex: 0})
    },
    [activateModule]
  )

  const saveAuthRoles = useCallback(
    (roles: AuthModel[], preferredUserRoleId?: number) => {
      if (!roles.length) {
        setAuth(undefined)
        authHelper.removeAuthRoles()
        return undefined
      }

      const responseModuleCode = roles.find((role) => role.moduleCode)?.moduleCode
      if (responseModuleCode) {
        activateModule(responseModuleCode)
      }

      const preferredIndex = preferredUserRoleId
        ? roles.findIndex((role) => role.userRoleId === preferredUserRoleId)
        : -1
      const activeRoleIndex = preferredIndex >= 0 ? preferredIndex : 0
      const activeAuth = roles[activeRoleIndex]
      authHelper.setAuthRoles({roles, activeRoleIndex})
      setAuth(activeAuth)
      return activeAuth
    },
    [activateModule]
  )

  const switchRole = useCallback(
    async (userRoleId: number | undefined, legacyRoleIndex: number) => {
      const authRoles = authHelper.getAuthRoles()
      if (!authRoles) {
        return false
      }

      let switched = false
      if (isModuleSession) {
        if (typeof userRoleId !== 'number') {
          return false
        }
        switched = authHelper.setActiveRoleByUserRoleId(userRoleId)
      } else if (authRoles.roles[legacyRoleIndex]) {
        authHelper.setActiveRole(legacyRoleIndex)
        switched = true
      }

      if (!switched) {
        return false
      }

      const nextAuth = authHelper.getActiveAuth()
      if (!nextAuth) {
        return false
      }

      setAuth(nextAuth)
      queryClient.clear()
      const {data: user} = await getUserByToken(nextAuth.token)
      setCurrentUser(user)
      return true
    },
    [isModuleSession, queryClient]
  )

  const logout = useCallback(() => {
    authHelper.removeAuthRoles()
    setAuth(undefined)
    setCurrentUser(undefined)
    queryClient.clear()
  }, [queryClient])

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        saveAuthRoles,
        switchRole,
        currentUser,
        setCurrentUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthProvider: FC<WithChildren> = ({children}) => (
  <ModuleProvider>
    <AuthStateProvider>{children}</AuthStateProvider>
  </ModuleProvider>
)

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    let cancelled = false

    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data} = await getUserByToken(apiToken)
          if (!cancelled && data?.tenantId > 0) {
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!cancelled && !didRequest.current) {
          logout()
        }
      } finally {
        if (!cancelled) {
          setShowSplashScreen(false)
        }
        didRequest.current = true
      }
    }

    if (auth?.token) {
      requestUser(auth.token)
    } else {
      logout()
      setShowSplashScreen(false)
      didRequest.current = true
    }

    return () => {
      cancelled = true
    }
    // Auth initialization intentionally runs once for the stored active session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
